import { useTranslation } from 'react-i18next';
import { rolePlaying } from '@/mocks/trainingData';

const SCENARIO_COUNT = 3;
const FEATURE_COUNT = 3;

const scenarioImages = [
  '/images/generated/roleplay-daily-2026.jpg',
  '/images/generated/roleplay-formal-2026.jpg',
  '/images/generated/roleplay-crisis-2026.jpg',
];

export default function RolePlaying() {
  const { t } = useTranslation();

  return (
    <section className="relative py-20 md:py-28 bg-primary-900">
      {/* Subtle background texture */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,215,0,0.03)_0%,transparent_70%)]"></div>

      <div className="relative max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {String(t('training.roleplay.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-400 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-50 tracking-[0.06em] mb-2">
            {t('training.roleplay.title')}
          </h2>
          {String(t('training.roleplay.sectionTitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-400 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto"></div>
          {String(t('training.roleplay.description')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-300 tracking-[0.04em] leading-relaxed max-w-[650px] mx-auto mt-5">{__s}</p>))}
        </div>

        {/* 3 Scenario Cards with progression arrows */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {Array.from({ length: SCENARIO_COUNT }, (_, idx) => (
            <div
              key={idx}
              className="bg-foreground-950/40 border border-white/8 group hover:border-accent-500/20 transition-all duration-300 flex flex-col relative"
            >
              {/* Level badge */}
              <div className="absolute top-3 right-3 z-10">
                <span className={`text-[9px] tracking-[0.1em] font-medium px-2 py-0.5 ${
                  idx === 0
                    ? 'bg-accent-500/20 text-accent-400 border border-accent-500/30'
                    : idx === 1
                      ? 'bg-accent-500/30 text-accent-300 border border-accent-500/40'
                      : 'bg-accent-500/40 text-accent-200 border border-accent-500/50'
                }`}>
                  {t(`training.roleplay.${idx}.level`)}
                </span>
              </div>

              {/* Image */}
              <div className="relative w-full aspect-[4/3] overflow-hidden">
                <img
                  src={scenarioImages[idx]}
                  alt={t(`training.roleplay.${idx}.title`)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>

              {/* Content */}
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
                    <i className={`${rolePlaying.scenarios[idx].icon} text-sm`}></i>
                  </div>
                  <h3 className="font-heading text-[16px] text-foreground-50 tracking-[0.05em] group-hover:text-accent-400 transition-colors">
                    {t(`training.roleplay.${idx}.title`)}
                  </h3>
                </div>
                <p className="text-[12px] text-accent-500/50 tracking-[0.1em] mb-3 ml-9">
                  {t(`training.roleplay.${idx}.titleEn`)}
                </p>
                <p className="text-[14px] text-foreground-300 tracking-[0.03em] leading-relaxed ml-9 flex-1">
                  {t(`training.roleplay.${idx}.description`)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Progression arrows for desktop */}
        <div className="hidden md:flex items-center justify-center gap-4 mb-14">
          <div className="flex items-center gap-2 text-accent-500/40">
            <span className="text-[12px] tracking-[0.1em]">{t('training.roleplay.level.0')}</span>
            <i className="ri-arrow-right-line text-xs"></i>
            <i className="ri-arrow-right-line text-xs"></i>
          </div>
          <div className="flex items-center gap-2 text-accent-500/60">
            <span className="text-[12px] tracking-[0.1em]">{t('training.roleplay.level.1')}</span>
            <i className="ri-arrow-right-line text-xs"></i>
            <i className="ri-arrow-right-line text-xs"></i>
          </div>
          <div className="flex items-center gap-2 text-accent-500/80">
            <span className="text-[12px] tracking-[0.1em]">{t('training.roleplay.level.2')}</span>
          </div>
        </div>

        {/* 3 Feature bullets */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-[900px] mx-auto">
          {Array.from({ length: FEATURE_COUNT }, (_, idx) => (
            <div key={idx} className="text-center">
              <div className="w-2 h-2 rounded-full bg-accent-500/50 mx-auto mb-3"></div>
              <h4 className="font-heading text-[15px] text-foreground-200 tracking-[0.05em] mb-2">
                {t(`training.roleplay.feature.${idx}.label`)}
              </h4>
              <p className="text-[13px] text-foreground-400 tracking-[0.03em] leading-relaxed">
                {t(`training.roleplay.feature.${idx}.text`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
